/**
 * Fetch-then-navigate for a back-step off the front of the loaded queue
 * (job #783). The fetch can take seconds; the fetch AND the navigation that
 * follows it run inside one runSeek bracket, which pauses the engine for the
 * duration (so the cursor cannot advance to cycle 1 underneath the fetch and
 * make "back" land on the current round's cycle 0) and restores the learner's
 * play intent on landing. A newer seek supersedes this one (isStale), in which
 * case the navigation is skipped — the newer seek owns the landing.
 *
 * `navigate` reads the cursor AFTER the fetch, so it always acts on where the
 * learner actually is. Returns true when it navigated.
 */
export async function navigateAfterEarlierRounds(opts: {
  runSeek: <T>(fn: (isStale: () => boolean) => Promise<T>) => Promise<T | undefined>
  /** Fetch and merge the earlier rounds. Failures are swallowed: navigate still runs. */
  fetchAndMerge: () => Promise<void>
  navigate: () => void
}): Promise<boolean> {
  // A seek that TIMES OUT lands (restores playback) and returns, but does not
  // bump the seek generation, so isStale() stays false and a fetch finishing
  // later would jump the learner backwards mid-playback (review #798). `active`
  // is cleared the moment the awaited seek returns, however it ended.
  let active = true
  try {
    const done = await opts.runSeek(async (isStale) => {
      try {
        await opts.fetchAndMerge()
      } catch (err) {
        console.warn('[navigateAfterEarlierRounds] could not load earlier rounds:', err)
      }
      if (!active || isStale()) return false
      opts.navigate()
      return true
    })
    return done === true
  } finally {
    active = false
  }
}
